import React, { useEffect, useState } from 'react';
import { IconCardSwipe, IconP2p } from '@americanexpress/dls-icons';
import { cn } from '@/helpers/utils';
import styles from '../LoungeDetails.module.scss';
import PolicyButton from '../../common/PolicyButton';
import { modalTypes } from '../../common/modals/constants';
import WaitListButton from '../../waitList/WaitListButton';
import LoungeDetailsCardPickerMessage from './LoungeDetailsCardPickerMessage';
import { policyButtonIds } from './constants';
import type { LoungeDetailsSidebarProps } from './types';

const DesktopLoungeDetailsSidebar = ({
  className,
  sidebarData,
}: LoungeDetailsSidebarProps) => {
  const {
    accessPolicyHeading,
    baseOneCmsImgUrl,
    cardImageSrc,
    guestPolicyHeading,
    hasAccessPolicy,
    hasGuestPolicy,
    hasWalletProduct,
    inlineCardMessage,
    isWaitListAvailableAtLounge,
    loggedInCardPill,
  } = sidebarData;
  const [isImageLoading, setIsImageLoading] = useState(true);

  useEffect(() => {
    setIsImageLoading(true);
    const image = new Image();
    image.src = cardImageSrc;

    if (image.complete) {
      setIsImageLoading(false);
    }
  }, [cardImageSrc]);

  return (
    <div
      className={cn(
        className,
        'margin-3-b',
        styles.loungeOverviewSidebarContainer
      )}
    >
      <div className="tw-relative tw-mt-5">
        <div className="tw-absolute -tw-top-5 tw-left-0 tw-right-0 tw-z-10 tw-mx-auto tw-flex tw-w-35">
          <span
            className="tw-flex-1 tw-bg-gradient-to-r tw-from-transparent tw-to-white"
            aria-hidden="true"
          />
          <div className="tw-flex-shrink-0 tw-bg-white tw-px-4">
            <img
              className={cn(
                isImageLoading && 'tw-invisible',
                'tw-block tw-h-10 tw-w-16 tw-rounded-sm tw-shadow-low'
              )}
              src={cardImageSrc}
              alt="Card Callout Notification"
              onLoad={() => setIsImageLoading(false)}
              onError={() => setIsImageLoading(false)}
            />
          </div>
          <span
            className="tw-flex-1 tw-bg-gradient-to-r tw-from-white tw-to-transparent"
            aria-hidden="true"
          />
        </div>
        <div className="tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-border-gray-300 tw-bg-white">
          <div className="tw-bg-white tw-px-8 tw-pb-6 tw-pt-8">
            <div className="tw-font-sans tw-text-xl">
              {hasWalletProduct && (
                <div className="tw-mb-3 tw-w-fit tw-rounded tw-bg-gradient-to-r tw-from-blue-deep-700 tw-to-blue-bright-shade-600-s8 tw-px-2 tw-py-1 tw-font-sans tw-text-sm tw-text-xl tw-font-bold tw-uppercase tw-text-white">
                  {loggedInCardPill}
                </div>
              )}
            </div>
            <LoungeDetailsCardPickerMessage
              text={inlineCardMessage}
              className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-x-2 tw-pb-4 tw-text-center tw-font-dls tw-text-base tw-font-normal tw-text-gray-800"
            />
            <hr className="width-auto tw-border-gray-300" aria-hidden="true" />
            <div>
              {hasAccessPolicy && (
                <>
                  <PolicyButton
                    id={policyButtonIds.desktop.access}
                    heading={accessPolicyHeading}
                    modalType={modalTypes.ACCESS_POLICY_MODAL}
                    icon={<IconCardSwipe size="md" color="inherit" />}
                  />
                  {hasGuestPolicy && (
                    <hr
                      className="width-auto tw-mx-0 tw-border-gray-300"
                      aria-hidden="true"
                    />
                  )}
                </>
              )}
              {hasGuestPolicy && (
                <PolicyButton
                  id={policyButtonIds.desktop.guest}
                  heading={guestPolicyHeading}
                  modalType={modalTypes.GUEST_POLICY_MODAL}
                  icon={<IconP2p size="md" color="inherit" />}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {isWaitListAvailableAtLounge && (
        <div
          className="tw-border-blue-deep-tint-t12 tw-rounded-2xl tw-bg-blue-deep-700 tw-bg-contain tw-bg-no-repeat"
          style={{
            backgroundImage: `url(${baseOneCmsImgUrl}amex-world-service-logo-accent.png)`,
          }}
        >
          <WaitListButton />
        </div>
      )}
    </div>
  );
};

export default DesktopLoungeDetailsSidebar;
